import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { comnEnvs, comnUtils } from "@/comn/utils";
import { Button, Grid, Group, Layout, Page } from "@/comn/components";
import { useFetch, useForm, useGrid, useStore, useToast, useResource, useModal, usePage } from "@/comn/hooks";
import {
    APIS,
    SF_XTRN_USR_SRCH,
    SG_XTRN_USR_EXCEL_LIST,
    SG_XTRN_USR_LIST,
    URLS,
} from "@/tra/tancis/ptli/usr/xtrnusr/services/PtliXtrnUsrService";
import dayjs from "dayjs";

export const PTLI0922001Q = (): JSX.Element => {
    const { pgeUid, menuId } = usePage({
        auth: ["auth_ptli0001", "auth_ptli0015", "auth_ptlianyADM"],
    });
    const { t } = useTranslation();
    const navigate = useNavigate();
    const modal = useModal();
    const toast = useToast();
    const { pgeStore, setStore, getStore } = useStore({ pgeUid: pgeUid });
    const [load, setLoad] = useState(false);

    // Track whether 'D' (Deleted) is selected !== 'D' (삭제됨) 선택 여부 추적 ==!
    const [isDeletedSelected, setIsDeletedSelected] = useState(false);

    useResource({
        defaultSchema: [{ area: "comnCd", comnCd: "PTL0001" }],
    });

    const form = {
        xtrnUsrListSrch: useForm({
            defaultSchema: SF_XTRN_USR_SRCH,
            defaultValues: {
                ...(pgeStore?.form || {}),
            },
        }),
    };

    const grid = {
        xtrnUsrList: useGrid({
            defaultSchema: SG_XTRN_USR_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
        xtrnUsrExcelList: useGrid({
            defaultSchema: SG_XTRN_USR_EXCEL_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
    };

    const fetch = {
        getXtrnUsrList: useFetch({
            api: (page = grid.xtrnUsrList.page) =>
                APIS.getXtrnUsrList(form.xtrnUsrListSrch.getValues(), page, grid.xtrnUsrList.size),
            enabled: comnUtils.isEmpty(form.xtrnUsrListSrch.errors),
            key: [grid.xtrnUsrList.page, grid.xtrnUsrList.size],
            onSuccess: () => {
                setStore(pgeUid, {
                    form: form.xtrnUsrListSrch.getValues(),
                    page: grid.xtrnUsrList.page,
                    size: grid.xtrnUsrList.size,
                });
            },
            onError: () => toast.showToast({ type: "error", content: "msg.com.00025" }),
            showToast: true,
        }),

        getXtrnUsrExcelList: useFetch({
            api: (data: any) => (load ? APIS.getXtrnUsrExcelList(data) : null),
            enabled: comnUtils.isEmpty(form.xtrnUsrListSrch.errors),
            onSuccess: () => {
                if (load) {
                    grid.xtrnUsrExcelList.exportExcel({
                        excelName: `${t("L_INT_PTL")}_${t("L_IMPPN_EXPPN")}_${t("T_USR_INFO")}_${dayjs().format("DD/MM/YYYY HHmm")}`,
                        sheetName: "Sheet1",
                    });
                }
                setLoad(false);
            },
            onError: () => {},
            showToast: true,
        }),
    };

    const render = {
        cell: {
            usrNm: (props: any) => {
                const { rowValues, value } = props;
                return <Link to={`/ptli/usr/xtrnusr/ptli0922004q/${rowValues.xtrnUsrUuid}`}>{value}</Link>;
            },
        },
    };

    /*
     * Handle status checkbox change
     * - If 'D' is selected → uncheck all others, disable them
     * - If any other is selected while 'D' was selected → uncheck 'D', enable all
     */
    const handler = {
        onUsrStatCdChange: (selectedValues: string[]) => {
            const deletedSelected = selectedValues.includes("D");
            const prevDeletedSelected = isDeletedSelected;

            if (deletedSelected && !prevDeletedSelected) {
                // 'D' was just selected → clear all others, keep only 'D'
                form.xtrnUsrListSrch.setValue("arrUsrStatCd", ["D"]);
                setIsDeletedSelected(true);
            } else if (!deletedSelected && prevDeletedSelected) {
                // Was on 'D', now switched to something else → allow normally
                setIsDeletedSelected(false);
            } else if (deletedSelected && prevDeletedSelected) {
                // 'D' is being unchecked
                form.xtrnUsrListSrch.setValue("arrUsrStatCd", selectedValues.filter((v) => v !== "D"));
                setIsDeletedSelected(false);
            } else {
                // Normal selection, no 'D' involved
                setIsDeletedSelected(false);
            }
        },

        onSrchBtnClick: (page: number = 0) => {
            grid.xtrnUsrList.setPage(page);
            fetch.getXtrnUsrList.fetch(page);
        },

        onNewBtnClick: () => navigate(`${URLS.ptli0922002s}`),
    };

    useEffect(() => {
        handler.onSrchBtnClick(pgeStore?.page);
    }, []);

    useEffect(() => {
        const store = getStore(pgeUid);
        if (!comnUtils.isEmpty(store) || load) {
            fetch.getXtrnUsrExcelList.fetch(store.form);
        }
    }, [load]);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.xtrnUsrListSrch.schema.xtrnUsrIdntfNo} />
                            <Group.Control {...form.xtrnUsrListSrch.schema.usrNm} />
                        </Group.Row>
                        <Group.Row>
                            {/* Pass onChange and disabled per-option based on isDeletedSelected */}
                            <Group.Control
                                {...form.xtrnUsrListSrch.schema.arrUsrStatCd}
                                all={true}
                                onChange={(selectedValues: string[]) => handler.onUsrStatCdChange(selectedValues)}
                                // When 'D' is selected, disable all options except 'D'
                                // When 'D' is not selected, all options are enabled
                                optionDisabled={(optionValue: string) =>
                                    isDeletedSelected && optionValue !== "D"
                                }
                            />
                            <Group.Control {...form.xtrnUsrListSrch.schema.frstRgsrDtm} />
                        </Group.Row>
                    </Group.Section>
                    <Layout>
                        <Layout.Left>
                            <Button role={"reset"} onClick={() => {
                                form.xtrnUsrListSrch.reset();
                                setIsDeletedSelected(false); // Reset deleted state on form reset
                            }} />
                        </Layout.Left>
                        <Layout.Right>
                            <Button role={"search"} onClick={() => handler.onSrchBtnClick()} />
                        </Layout.Right>
                    </Layout>
                </Group.Body>
            </Group>
            <Group>
                <Group.Body>
                    <Layout direction="row">
                        <Layout.Left>
                            <Button role="excelDown" onClick={() => setLoad(true)} />
                        </Layout.Left>
                    </Layout>
                    <Group.Section>
                        <Grid
                            {...grid.xtrnUsrList.grid}
                            data={fetch.getXtrnUsrList.data?.xtrnUsrList}
                            render={render}
                        />
                    </Group.Section>
                </Group.Body>
            </Group>
            <Layout>
                <Layout.Left>
                    <Layout.Button role={"new"} onClick={handler.onNewBtnClick} />
                </Layout.Left>
            </Layout>
            <Group hidden>
                <Grid {...grid.xtrnUsrExcelList.grid} data={fetch.getXtrnUsrExcelList.data?.xtrnUsrExcelList} />
            </Group>
        </Page>
    );
};